import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { patientApi } from '@services/patient.service'
import { PatientCreateRequest, PatientSearchParams } from '@/types'

const PATIENTS_KEY = 'patients'

export function usePatients(params?: PatientSearchParams) {
  return useQuery({
    queryKey: [PATIENTS_KEY, params],
    queryFn: () => patientApi.getPatients(params),
  })
}

export function usePatient(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [PATIENTS_KEY, id],
    queryFn: () => patientApi.getPatient(id),
    enabled: options?.enabled ?? !!id,
  })
}

export function useCreatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PatientCreateRequest) => patientApi.createPatient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] })
    },
  })
}

export function useUpdatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PatientCreateRequest> }) => patientApi.updatePatient(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, variables.id] })
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] })
    },
  })
}

export function useDeletePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      patientApi.deletePatient(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] })
    },
  })
}

export function usePatientStatistics() {
  return useQuery({
    queryKey: ['patient-statistics'],
    queryFn: () => patientApi.getStatistics(),
  })
}

export function useSignIdentityCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (patientId: string) => patientApi.signIdentityCard(patientId),
    onSuccess: (_, patientId) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, patientId] })
    },
  })
}

export function useAppendIdentityAddendum() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      patientId,
      payload,
    }: {
      patientId: string
      payload: {
        field: string
        field_label?: string | null
        previous_value?: string | null
        new_value: string
        note?: string | null
      }
    }) => patientApi.appendIdentityAddendum(patientId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, variables.patientId] })
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] })
    },
  })
}

export function useAppendDiagnosisSummary() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ patientId, text }: { patientId: string; text: string }) =>
      patientApi.appendDiagnosisSummary(patientId, text),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, variables.patientId] })
    },
  })
}

export function useAppendCurrentPlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ patientId, text }: { patientId: string; text: string }) =>
      patientApi.appendCurrentPlan(patientId, text),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, variables.patientId] })
    },
  })
}

export function useUploadPatientPhoto() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ patientId, file }: { patientId: string; file: File }) =>
      patientApi.uploadPhoto(patientId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY, variables.patientId] })
    },
  })
}
